import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { libraryBestsellers } from '@/mocks/neurobaby';

export default function ColeccionBestSellers() {
  const { t, i18n } = useTranslation();

  // Checkout de Hotmart del pack de los 4 Best Sellers segun el idioma de la pagina
  const packCheckoutUrl =
    i18n.language === 'en'
      ? 'https://pay.hotmart.com/J107377305V'
      : 'https://pay.hotmart.com/P107377285E';

  const packImage = i18n.language === 'en'
    ? 'https://storage.helloreaddy.io/project_files/33950b86-7657-493b-8177-e5932d2de9a1/f6906bf2-25dd-4ab2-bb9b-e4df0e9a6939_compressed_ChatGPT-Image-10-ago-2026-01_12_19-a.m.-2.webp'
    : 'https://storage.helloreaddy.io/project_files/33950b86-7657-493b-8177-e5932d2de9a1/f03ac408-7068-43f1-9d57-1aeb656c1691_compressed_ChatGPT-Image-10-ago-2026-01_11_26-a.m.-3.webp';

  const getBookImage = (book: typeof libraryBestsellers[0]) => {
    return i18n.language === 'en' && book.imageEn ? book.imageEn : book.image;
  };

  return (
    <div className="min-h-screen bg-cream">
      <Navbar />

      {/* Hero */}
      <section className="pt-28 md:pt-36 pb-10 md:pb-14 px-6 md:px-10">
        <div className="w-full max-w-7xl mx-auto text-center">
          <span className="inline-block font-sans text-xs font-semibold text-terracotta uppercase tracking-[0.2em] mb-4">
            {t('coleccion_bs.hero.badge')}
          </span>
          <h1 className="font-serif text-4xl md:text-6xl font-bold text-[#8B6F5C] leading-tight">
            {t('coleccion_bs.hero.title1')}
            <br />
            {t('coleccion_bs.hero.title2')}
          </h1>
          <p className="font-sans text-base md:text-lg text-[#3D2B1F] max-w-2xl mx-auto mt-6 leading-relaxed">
            {t('coleccion_bs.hero.desc')}
          </p>
        </div>
      </section>

      {/* Los 4 libros */}
      <section className="pb-12 md:pb-16 px-6 md:px-10">
        <div className="w-full max-w-7xl mx-auto">
          <div className="text-center mb-10 md:mb-12">
            <span className="inline-block font-sans text-xs font-semibold text-[#5C4A3A] uppercase tracking-[0.2em] mb-3">
              {t('coleccion_bs.section.tag')}
            </span>
            <h2 className="font-serif text-2xl md:text-4xl font-bold text-[#8B6F5C] leading-tight mb-3">
              {t('coleccion_bs.section.title')}
            </h2>
            <p className="font-sans text-sm text-[#3D2B1F] max-w-2xl mx-auto">
              {t('coleccion_bs.section.desc')}
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
            {libraryBestsellers.map((book) => (
              <div
                key={book.id}
                className="bg-white rounded-2xl overflow-hidden border border-[#DCCDBF] transition-all duration-300 hover:border-terracotta/30 flex flex-col"
              >
                <div className="w-full aspect-[3/4] overflow-hidden bg-cream">
                  <img
                    src={getBookImage(book)}
                    alt={t(`library.bestsellers.${book.id}.title`, book.title)}
                    className="w-full h-full object-contain"
                  />
                </div>
                <div className="p-5 flex flex-col flex-grow">
                  <span className="font-sans text-[11px] text-terracotta font-semibold mb-1">
                    {t(`library.bestsellers.${book.id}.subtitle`, book.subtitle)}
                  </span>
                  <h3 className="font-serif text-base md:text-lg font-bold text-[#8B6F5C] leading-snug mb-2">
                    {t(`library.bestsellers.${book.id}.title`, book.title)}
                  </h3>
                  <p className="font-sans text-xs text-[#3D2B1F] leading-relaxed mb-4 flex-grow">
                    {t(`library.bestsellers.${book.id}.description`, book.description)}
                  </p>
                  <Link
                    to={book.route}
                    className="inline-flex items-center justify-center bg-terracotta text-white px-4 py-2 rounded-full font-sans text-xs font-semibold hover:bg-brown transition-colors duration-300 whitespace-nowrap"
                  >
                    {t('common.conocer_mas')}
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA comprar colección */}
      <section className="pb-16 md:pb-24 px-6 md:px-10">
        <div className="w-full max-w-4xl mx-auto">
          <div className="bg-white rounded-3xl overflow-hidden border border-[#DCCDBF] flex flex-col sm:flex-row items-center gap-6 p-6 md:p-8">
            <div className="w-full h-auto sm:w-[170px] sm:h-[170px] flex-shrink-0 overflow-hidden rounded-2xl">
              <img
                src={packImage}
                alt={t('coleccion_bs.hero.title1')}
                className="w-full h-auto object-contain sm:h-full sm:object-cover"
              />
            </div>
            <div className="flex-1 text-center sm:text-left">
              <h2 className="font-serif text-xl md:text-2xl font-bold text-[#8B6F5C] mb-2 leading-tight">
                {t('coleccion_bs.cta.title')}
              </h2>
              <p className="font-sans text-sm text-[#3D2B1F] mb-5 leading-relaxed">
                {t('coleccion_bs.cta.desc')}
              </p>
              <a
                href={packCheckoutUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 bg-terracotta text-white px-8 py-3 rounded-full font-sans text-sm font-semibold hover:bg-brown transition-colors duration-300 whitespace-nowrap cursor-pointer"
              >
                {t('common.comprar_coleccion')}
                <i className="ri-arrow-right-line" />
              </a>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}